import React, { useState } from 'react';
import { GPTMessage } from '.';
import Markdown from 'react-markdown';
import { FaRobot, FaRegClipboard, FaClipboardCheck } from 'react-icons/fa';
import {
  FilePdf,
  PaperclipHorizontal,
  Image,
  FileText,
} from '@phosphor-icons/react';
import { getFileTypeInfo } from '@Utils/FileAttachmentUtils';
import './ChatBubble.css';

interface Props {
  message: GPTMessage;
}

type FileRef = {
  type: 'image' | 'pdf' | 'text' | 'document';
  key: string;
  fullMatch: string;
};

export const parseFileReferences = (message: string): FileRef[] => {
  const patterns = [
    { type: 'image' as const, regex: /\[image:([^\]]+)\]/g },
    { type: 'pdf' as const, regex: /\[pdf:([^\]]+)\]/g },
    { type: 'text' as const, regex: /\[text:([^\]]+)\]/g },
    { type: 'document' as const, regex: /\[document:([^\]]+)\]/g },
  ];

  return patterns.flatMap(({ type, regex }) =>
    Array.from(message.matchAll(regex)).map((match) => ({
      type,
      key: match[1],
      fullMatch: match[0],
    }))
  );
};
const getFileIcon = (fileName: string) => {
  const fileTypeInfo = getFileTypeInfo(fileName);
  switch (fileTypeInfo.type) {
    case 'image':
      return <Image size={18} className="text-primary-700" />;
    case 'pdf':
      return <FilePdf size={18} className="text-primary-700" />;
    case 'text':
      return <FileText size={18} className="text-primary-700" />;
    default:
      return <PaperclipHorizontal size={18} className="text-primary-700" />;
  }
};

const ChatBubble: React.FC<Props> = ({ message }) => {
  const isUser = message.sender === 'you';
  const formattedTime = message.dateSent.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    month: 'short',
    day: 'numeric',
  });

  const { fileName } = message;
  const fileNames = fileName;
  const [copied, setCopied] = useState<string>('Copy');
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const fileReferences = parseFileReferences(message.message);

  let displayMessage = message.message;
  fileReferences.forEach((ref) => {
    displayMessage = displayMessage.replace(ref.fullMatch, '');
  });
  displayMessage = displayMessage.trim();

  const handleCopy = async () => {
    const formattedText = displayMessage.replace(/([*_#-]+|`{3})/g, '');
    navigator.clipboard.writeText(formattedText);

    setCopied('Copied!');
    setTimeout(() => {
      setCopied('Copy');
      setIsTooltipVisible(false);
    }, 2000);
  };

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 mr-2 mt-4`}
    >
      <div
        className={`flex flex-col ${
          isUser ? 'items-end' : 'items-start'
        } w-[80%] max-w-[100vw]`}
      >
        <div className="flex items-start justify-start">
          {!isUser && (
            <div>
              <FaRobot className="me-2 mt-3 fill-primary-300" size={25} />
            </div>
          )}
          <div
            className={`rounded-2xl p-3 shadow-lg ${
              isUser ? 'bg-primary-300 text-white' : 'bg-white text-primary-700'
            }`}
          >
            {fileReferences.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {fileReferences.map((fileRef, index) => {
                  const FileName =
                    fileNames && fileNames[index]
                      ? fileNames[index]
                      : fileRef.key;
                  return (
                    <div
                      key={index}
                      className="flex w-fit items-center gap-2 rounded-lg bg-blue-100 px-3 py-1.5"
                    >
                      {getFileIcon(FileName)}
                      <span className="text-xs text-primary-700">
                        {FileName}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <Markdown className="markdown text-sm">{displayMessage}</Markdown>
          </div>
          <div className="relative ms-1 flex items-center justify-center">
            {/*Clipboard */}
            {!isUser &&
              (copied == 'Copy' ? (
                <FaRegClipboard
                  onMouseEnter={() => setIsTooltipVisible(true)}
                  onMouseLeave={() => setIsTooltipVisible(false)}
                  onClick={() => handleCopy()}
                  className="mt-3 cursor-pointer fill-gray-400 hover:fill-primary-600"
                  size={20}
                />
              ) : (
                <FaClipboardCheck
                  onClick={() => handleCopy()}
                  className="mt-3 cursor-pointer fill-gray-400 hover:fill-primary-600"
                  size={20}
                />
              ))}

            {/* Tooltip */}
            {isTooltipVisible && (
              <div className="absolute -bottom-10 flex flex-col items-center">
                {/* Pointer Arrow */}
                <div className="mb-[-8px] h-3 w-3 rotate-45 bg-primary-600"></div>
                <div className="rounded-md bg-primary-600 px-2 py-1 text-xs text-white shadow-md">
                  {copied}
                </div>
              </div>
            )}
          </div>
        </div>

        {/*Timeline */}
        <div
          className={`mt-1 flex gap-5 text-xs ${
            isUser ? 'text-right text-gray-500' : 'ms-8 text-left text-gray-500'
          }`}
        >
          {formattedTime}
        </div>
      </div>
    </div>
  );
};

export default ChatBubble;
